import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../components/AnimatedModal";
import { HiOutlineXMark, HiOutlineUserCircle } from "react-icons/hi2";

interface EditUserModalProps {
  user: any;
  onClose: () => void;
  onSubmit: (updatedUser: any) => void | Promise<void>;
}

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors";

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    role: user?.role === "organization" ? "admin" : user?.role || "member",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({ ...user, ...formData });
      onClose();
    } catch {
      // Parent handles error display; modal stays open
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full px-4 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
  const labelClass =
    "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {user?.profilePicture ? (
              <img
                src={user.profilePicture}
                alt=""
                className="w-11 h-11 rounded-xl object-cover shrink-0 ring-1 ring-gray-200/80 dark:ring-gray-600"
              />
            ) : (
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
                <HiOutlineUserCircle className="w-6 h-6" strokeWidth={2} />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate">
                {t("edit_user")}
              </h2>
              {displayName && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {displayName}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className={labelClass}>
                  {t("first_name")}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={inputBase}
                  required
                  maxLength={80}
                />
              </div>
              <div>
                <label htmlFor="lastName" className={labelClass}>
                  {t("last_name")}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={inputBase}
                  required
                  maxLength={80}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className={labelClass}>
                {t("email")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={inputBase}
                required
                maxLength={150}
              />
            </div>

            <div>
              <label htmlFor="role" className={labelClass}>
                {t("role")}
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={`${inputBase} cursor-pointer`}
              >
                <option value="admin">{t("admin")}</option>
                <option value="member">{t("member")}</option>
              </select>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-900/20 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 shrink-0">
            <button type="button" onClick={onClose} className={glassBtnMuted}>
              {t("cancel")}
            </button>
            <button type="submit" disabled={loading} className={`${glassBtn} sm:ml-auto`}>
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
                  {t("saving")}
                </>
              ) : (
                t("save")
              )}
            </button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
};

export default EditUserModal;
